import React from "react";

import { Box, ButtonBase, Paper, Typography } from "@material-ui/core";
import { alpha, makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "stretch",
    minWidth: 0,
    minHeight: 68,
    overflowX: "auto",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.divider, 0.92)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
    ...theme.scrollbarStylesSoft,
  },
  item: {
    position: "relative",
    display: "flex",
    flex: "1 0 132px",
    minWidth: 0,
    alignItems: "flex-start",
    justifyContent: "center",
    flexDirection: "column",
    padding: theme.spacing(1.25, 1.75),
    textAlign: "left",
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    "&:last-child": {
      borderRight: 0,
    },
    [theme.breakpoints.down("xs")]: {
      flexBasis: 124,
      padding: theme.spacing(1, 1.25),
    },
  },
  interactive: {
    color: "inherit",
    transition: theme.transitions.create(["background-color", "color"], {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover, &:focus-visible": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selected: {
    backgroundColor: theme.palette.action.selected,
    "&::after": {
      content: '\"\"',
      position: "absolute",
      left: theme.spacing(1.5),
      right: theme.spacing(1.5),
      bottom: 0,
      height: 2,
      backgroundColor: theme.palette.primary.main,
    },
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: "0.72rem",
    fontWeight: 600,
    lineHeight: 1.25,
  },
  value: {
    marginTop: theme.spacing(0.4),
    color: theme.palette.text.primary,
    fontSize: "1.25rem",
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  helper: {
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    fontSize: "0.68rem",
    lineHeight: 1.2,
  },
  success: { color: theme.palette.success.main },
  warning: { color: theme.palette.warning.main },
  error: { color: theme.palette.error.main },
  info: { color: theme.palette.info.main },
}));

const MetricsStrip = ({ items = [], ariaLabel = "Indicadores", className = "" }) => {
  const classes = useStyles();
  const visibleItems = items.filter(
    (item) => item && item.key != null && item.label,
  );

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <Paper
      component="section"
      aria-label={ariaLabel}
      elevation={0}
      className={`${classes.root} ${className}`.trim()}
    >
      {visibleItems.map((item) => {
        const content = (
          <>
            <Typography component="span" className={classes.label}>
              {item.label}
            </Typography>
            <Typography
              component="strong"
              className={`${classes.value} ${
                item.severity && classes[item.severity]
                  ? classes[item.severity]
                  : ""
              }`.trim()}
            >
              {item.value ?? "—"}
            </Typography>
            {item.helper ? (
              <Typography component="span" className={classes.helper}>
                {item.helper}
              </Typography>
            ) : null}
          </>
        );
        const itemClassName = `${classes.item} ${
          item.onClick ? classes.interactive : ""
        } ${item.selected ? classes.selected : ""}`.trim();

        return item.onClick ? (
          <ButtonBase
            key={item.key}
            className={itemClassName}
            aria-label={item.ariaLabel || `${item.label}: ${item.value}`}
            aria-pressed={Boolean(item.selected)}
            onClick={item.onClick}
          >
            {content}
          </ButtonBase>
        ) : (
          <Box key={item.key} className={itemClassName}>
            {content}
          </Box>
        );
      })}
    </Paper>
  );
};

export default MetricsStrip;
